(() => {
  const clamp = (v, a, b) => Math.min(Math.max(v, a), b);

  class PinSlider extends HTMLElement {
    static get observedAttributes() {
      return ['gap', 'height', 'snap', 'inset', 'col', 'slide-height', 'slide2-hang'];
    }

    constructor() {
      super();
      this.attachShadow({ mode: 'open' });

      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            position: relative;
            height: 100vh;
            contain: content;
          }
          .sticky {
            position: sticky;
            top: 0;
            height: var(--ps-height, 100vh);
            overflow: clip;
            background: var(--ps-bg, transparent);
          }
          .viewport {
            position: relative;
            width: 100%;
            height: 100%;
            display: grid;
            place-items: center;
          }
          .track {
            display: grid;
            grid-auto-flow: column;
            grid-auto-columns: var(--ps-col, 100vw);
            align-items: center;
            gap: var(--ps-gap, 2rem);
            will-change: transform;
            transform: translateX(0px);
          }
          ::slotted(*) {
            height: var(--ps-slide-height, 100vh);
            display: flex;
            justify-content: center;
            align-items: center;
            overflow: hidden;
          }
          ::slotted(.image-banner) {
            width: 100%;
          }
        </style>
        <div class="sticky">
          <div class="viewport">
            <div class="track"><slot></slot></div>
          </div>
        </div>
      `;

      this.elSticky = this.shadowRoot.querySelector('.sticky');
      this.elTrack = this.shadowRoot.querySelector('.track');

      this.run = 0;
      this.start = 0;
      this.end = 0;
      this.ticking = false;
      this.slides = [];
      this.snapCount = 0;
      this.insetPx = 0;
      this.slide2Hang = 0;

      this.onScroll = this.onScroll.bind(this);
      this.onRAF = this.onRAF.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);

      this.ro = null;
      this.mo = null;
    }

    connectedCallback() {
      this.applyAttributes();

      queueMicrotask(() => {
        this.collectSlides();
        this.measure();
        this.update();

        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onResize, { passive: true });
        this.addEventListener('focusin', this.onFocusIn);

        this.ro = new ResizeObserver(() => { this.measure(); this.request(); });
        this.ro.observe(this);
        this.ro.observe(this.elTrack);

        this.mo = new MutationObserver(() => { this.collectSlides(); this.measure(); this.request(); });
        this.mo.observe(this, { childList: true, subtree: true });
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      this.removeEventListener('focusin', this.onFocusIn);
      if (this.ro) this.ro.disconnect();
      if (this.mo) this.mo.disconnect();
    }

    attributeChangedCallback() {
      this.applyAttributes();
      this.measure();
      this.request();
    }

    refresh() {
      this.collectSlides();
      this.measure();
      this.update();
    }

    applyAttributes() {
      const gap = this.getAttribute('gap');
      this.style.setProperty('--ps-gap', gap || '2rem');

      const height = this.getAttribute('height');
      this.style.setProperty('--ps-height', height || '100vh');

      const col = this.getAttribute('col');
      this.style.setProperty('--ps-col', col || '100vw');

      const slideH = this.getAttribute('slide-height');
      this.style.setProperty('--ps-slide-height', slideH || '80vh');

      const snapAttr = this.getAttribute('snap');
      if (snapAttr === 'auto') this.snapCount = -1;
      else this.snapCount = Number.isFinite(parseInt(snapAttr, 10)) ? Math.max(0, parseInt(snapAttr, 10)) : 0;

      const inset = parseFloat(this.getAttribute('inset') || '0');
      this.insetPx = Number.isFinite(inset) ? Math.max(0, inset) : 0;
      
      const slide2Hang = parseFloat(this.getAttribute('slide2-hang') || '0');
      this.slide2Hang = Number.isFinite(slide2Hang) ? Math.max(0, Math.min(1, slide2Hang)) : 0;
    }

    collectSlides() {
      const slot = this.shadowRoot.querySelector('slot');
      this.slides = slot ? slot.assignedElements({ flatten: true }) : [];
      this.slides.forEach((el, i) => {
        el.dataset.slideIndex = String(i);
      });
    }

    onFocusIn(e) {
      if (!e.target.matches(':focus-visible') || window.scrollY >= this.end) return;
      window.scrollTo({ top: this.end, behavior: 'instant' });
      this.update();
    }

    onResize() { this.measure(); this.request(); }
    onScroll() { this.request(); }

    request() {
      if (!this.ticking) {
        this.ticking = true;
        requestAnimationFrame(this.onRAF);
      }
    }

    measure() {
      const trackWidth = this.elTrack.scrollWidth;
      const viewport = this.getBoundingClientRect().width || window.innerWidth;
      this.run = Math.max(0, trackWidth - viewport);

      const stickyH = this.elSticky.getBoundingClientRect().height || window.innerHeight;
      const deadScrollZone = window.innerHeight * 0.5; // 50vh additional scroll
      const hostH = stickyH + (this.run * 2) + deadScrollZone - this.insetPx;
      this.style.height = `${hostH}px`;

      const rect = this.getBoundingClientRect();
      this.start = rect.top + window.scrollY;
      this.end = this.start + (this.run * 2) - this.insetPx;

      if (this.snapCount === -1) this.snapCount = this.slides.length;
    }

    onRAF() { this.ticking = false; this.update(); }

    update() {
      const y = window.scrollY;
      let x;

      if (y <= this.start) x = 0;
      else if (y >= this.end) x = -this.run;
      else {
        const p = clamp((y - this.start) / (this.end - this.start), 0, 1);
        const snapped = this.snapCount > 1
          ? Math.round(p * (this.snapCount - 1)) / (this.snapCount - 1)
          : p;
        x = -this.run * snapped;
      }

      this.elTrack.style.transform = `translateX(${x}px)`;

      const p = this.run === 0 ? 1 : clamp((y - this.start) / (this.end - this.start), 0, 1);
      this.dispatchEvent(new CustomEvent('pin-slider:progress', {
        bubbles: true,
        detail: { progress: p, x, run: this.run }
      }));

      const n = this.slides.length;
      if (n === 0) return;

      let adjustedP = p;
      if (n > 1 && this.slide2Hang > 0) {
        const hangZone = this.slide2Hang;
        const slide1End = 1 / (n - 1);
        const slide2End = 2 / (n - 1);
        
        if (p >= slide1End && p <= slide2End) {
          const localProgress = (p - slide1End) / (slide2End - slide1End);
          if (localProgress <= hangZone) {
            adjustedP = slide1End + (localProgress / hangZone) * (slide2End - slide1End) * 0.3;
          } else {
            const remainingProgress = (localProgress - hangZone) / (1 - hangZone);
            adjustedP = slide1End + 0.3 * (slide2End - slide1End) + remainingProgress * 0.7 * (slide2End - slide1End);
          }
        }
      }
      
      this.slides.forEach((slide, i) => {
        let lp;
        if (n === 1) {
          lp = adjustedP;
        } else {
          const segmentSize = 1 / n;
          const segmentStart = i * segmentSize;
          const segmentEnd = (i + 1) * segmentSize;
          
          if (adjustedP < segmentStart) {
            lp = 0;
          } else if (adjustedP > segmentEnd) {
            lp = 1;
          } else {
            lp = (adjustedP - segmentStart) / segmentSize;
          }
        }

        slide.style.setProperty('--slide-progress', lp.toFixed(4));
        if (lp >= 1 - 1e-5) {
          slide.setAttribute('data-slide-progress', '1');
        } else {
          slide.removeAttribute('data-slide-progress');
        }

        const wasActive = slide.classList.contains('is-active');
        const isActive = lp > 0 && lp < 1 || (i === 0 && lp === 0) || (i === n - 1 && lp === 1);
        if (isActive && !wasActive) slide.classList.add('is-active');
        if (!isActive && wasActive) slide.classList.remove('is-active');

        slide.dispatchEvent(new CustomEvent('pin-slider:slideprogress', {
          bubbles: true,
          detail: { index: i, progress: lp, global: adjustedP, originalGlobal: p }
        }));
      });
    }
  }

  if (!customElements.get('pin-slider')) {
    customElements.define('pin-slider', PinSlider);
  }

})();